import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Input, Select, Switch, InputNumber, Button, Alert } from 'antd'
import {
  dutyKbUrl,
  postRequest,
  patchRequest,
  successNotification,
  errorNotification
} from '../../../config'
import { buildKbPayload } from '../utils'

const { Option } = Select

// Topics mirror the w-api DutyKnowledgeEntry.topic choices (w-api KB_TOPICS).
export const TOPICS = [
  { value: 'policy', label: 'Policy' },
  { value: 'scope', label: 'Scope' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'how_it_works', label: 'How it works' },
  { value: 'faq', label: 'FAQ' },
  { value: 'escalation', label: 'Escalation' }
]

// Services mirror the w-api DutyKnowledgeEntry.service choices (w-api KB_SERVICES). One assistant
// answers for all four; it only selects an entry for the service it is tagged with, plus the
// "general" ones, so the wrong service here means the entry is never used for the right question.
export const SERVICES = [
  { value: 'duty', label: 'Import duty' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'tracking', label: 'Shipment tracking' },
  { value: 'cars', label: 'Car sales' },
  { value: 'general', label: 'General (the company)' }
]

// Create (POST) when no record, edit (PATCH) when a record is supplied.
const KnowledgeForm = ({ record, handleDone }) => {
  const [loading, setLoading] = useState(false)
  const isEdit = !!(record && record.id)

  const onFinish = async values => {
    const payload = buildKbPayload(values)
    const response = isEdit
      ? await patchRequest(
          `${dutyKbUrl}/${record.id}/`,
          payload,
          'knowledge entry',
          setLoading
        )
      : await postRequest(dutyKbUrl, payload, 'knowledge entry', setLoading)

    if (response.success) {
      successNotification(isEdit ? 'Entry updated' : 'Entry created')
      handleDone && handleDone()
    } else {
      errorNotification(
        response.error?.response?.data?.message || 'Could not save the entry, retry.'
      )
    }
  }

  return (
    <Form
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        title: record?.title,
        topic: record?.topic || 'faq',
        service: record?.service || 'duty',
        trigger: record?.trigger,
        content: record?.content,
        isAlwaysOn: record?.isAlwaysOn || false,
        priority: record?.priority || 0,
        isActive: record?.isActive !== undefined ? record.isActive : true
      }}
    >
      {(record?.isAlwaysOn || undefined) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Always-on policy entry"
          description="This entry is sent to the assistant every relevant turn (e.g. the paid-service policy). Edit with care."
        />
      )}

      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: 'Title is required' }]}
      >
        <Input placeholder="e.g. Service is paid (no free estimates)" />
      </Form.Item>

      <Form.Item
        name="topic"
        label="Topic"
        rules={[{ required: true, message: 'Topic is required' }]}
      >
        <Select>
          {TOPICS.map(t => (
            <Option key={t.value} value={t.value}>
              {t.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="service"
        label="Service"
        extra="Which service this entry is about. The assistant only uses it for questions about that service; General entries are used for all of them."
        rules={[{ required: true, message: 'Service is required' }]}
      >
        <Select>
          {SERVICES.map(s => (
            <Option key={s.value} value={s.value}>
              {s.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="trigger"
        label="Trigger (keywords / questions this entry answers)"
      >
        <Input.TextArea rows={2} placeholder="free discount cheap waive ..." />
      </Form.Item>

      <Form.Item
        name="content"
        label="Content (the grounded answer)"
        rules={[{ required: true, message: 'Content is required' }]}
      >
        <Input.TextArea rows={5} placeholder="The answer the assistant may use ..." />
      </Form.Item>

      <Form.Item
        name="isAlwaysOn"
        label="Always on (include every relevant turn)"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name="priority"
        label="Priority (orders entries that are equally relevant)"
      >
        <InputNumber min={0} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="isActive" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block>
          {isEdit ? 'Save changes' : 'Create entry'}
        </Button>
      </Form.Item>
    </Form>
  )
}

KnowledgeForm.propTypes = {
  record: PropTypes.object,
  handleDone: PropTypes.func
}

export default KnowledgeForm
